import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';

const fontFamily = {
  fontFamily: "'Poppins', sans-serif",
};

const baseLinkStyle = {
  ...fontFamily,
  textDecoration: 'none',
  color: '#000000',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '1.4',
  transition: 'all 0.3s ease',
  whiteSpace: 'nowrap',
};

const Header = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [buttonHover, setButtonHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isHamburgerHover, setIsHamburgerHover] = useState(false);
  const [isAIDropdownOpen, setIsAIDropdownOpen] = useState(false);
  const [isPremiumDropdownOpen, setIsPremiumDropdownOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isMobileAIDropdownOpen, setIsMobileAIDropdownOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [isMobilePremiumDropdownOpen, setIsMobilePremiumDropdownOpen] = useState(false);
  
  // Services dropdown states
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesDropdownOpen, setIsMobileServicesDropdownOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    const handleScroll = () => setScrolled(window.scrollY > 50);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.ai-dropdown-container')) {
        setIsAIDropdownOpen(false);
        setOpenSubmenu(null);
      }
      if (!event.target.closest('.premium-solutions-dropdown-container')) {
        setIsPremiumDropdownOpen(false);
      }
      if (!event.target.closest('.services-dropdown-container')) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getLinkStyle = (name, path) => ({
    ...baseLinkStyle,
    color:
      location.pathname === path
        ? '#F26522'
        : hoveredLink === name
        ? '#F26522'
        : baseLinkStyle.color,
    padding: isMobile ? '10px 0' : '0',
    display: 'block',
  });

  const headerStyle = {
    ...fontFamily,
    position: 'sticky',
    top: 0,
    width: '100%',
    backgroundColor: '#ffffff',
    boxShadow: scrolled
      ? '0 4px 12px rgba(0, 0, 0, 0.1)'
      : '0 2px 4px rgba(0,0,0,0.05)',
    zIndex: 1000,
    transition: 'all 0.3s ease',
    padding: scrolled ? '10px 20px' : '20px 20px',
    boxSizing: 'border-box',
  };

  const navStyle = {
    ...fontFamily,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  };

  const logoStyle = {
    height: scrolled ? '45px' : '70px',
    transition: 'all 0.3s ease',
  };

  const navLinksContainer = {
    display: 'flex',
    gap: scrolled ? '16px' : '20px',
    alignItems: 'center',
    position: 'relative',
    flexWrap: 'nowrap',
  };

  const buttonStyle = {
    ...fontFamily,
    backgroundColor: buttonHover ? '#d3541c' : '#F26522',
    color: '#ffffff',
    padding: '10px 24px',
    borderRadius: '28px',
    border: 'none',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    letterSpacing: '0.4px',
    transition: 'all 0.3s ease',
  };

  const hamburgerStyle = {
    display: isMobile ? 'block' : 'none',
    cursor: 'pointer',
    fontSize: '26px',
    background: 'none',
    border: 'none',
    color: isHamburgerHover ? '#F26522' : '#333',
    margin: '0 10px',
    transition: 'color 0.3s ease',
  };

  const mobileMenuStyle = {
    display: menuOpen ? 'flex' : 'none',
    flexDirection: 'column',
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    padding: '0',
    backgroundColor: '#fff',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    zIndex: 999,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    borderRadius: '0 0 12px 12px',
    border: '1px solid #f0f0f0',
    maxHeight: '80vh',
    overflowY: 'auto',
  };

  const mobileMenuItemStyle = {
    ...baseLinkStyle,
    padding: '16px 20px',
    borderBottom: '1px solid #f5f5f5',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    backgroundColor: '#fff',
    fontWeight: 500,
    fontSize: '14px',
    position: 'relative',
  };

  const mobileMenuItemHoverStyle = {
    backgroundColor: '#f8f9fa',
    color: '#F26522',
    borderLeft: '4px solid #F26522',
    paddingLeft: '16px',
  };

  const mobileSubMenuStyle = {
    backgroundColor: '#f8f9fa',
    borderLeft: '2px solid #F26522',
    marginLeft: '20px',
    borderRadius: '0 8px 8px 0',
  };

  const mobileSubMenuItemStyle = {
    ...baseLinkStyle,
    padding: '12px 16px',
    fontSize: '13px',
    color: '#555',
    borderBottom: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
  };

  const mobileNestedSubMenuStyle = {
    backgroundColor: '#f1f3f4',
    borderLeft: '2px solid #F26522',
    marginLeft: '16px',
    borderRadius: '0 6px 6px 0',
  };

  const mobileNestedSubMenuItemStyle = {
    ...baseLinkStyle,
    padding: '10px 14px',
    fontSize: '12px',
    color: '#666',
    borderBottom: '1px solid #e0e0e0',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
  };

  return (
    <header style={headerStyle}>
      <nav style={navStyle}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ marginRight: '10px' }}>
            <img src={logo} alt="Logo" style={logoStyle} />
          </Link>
        </div>

        {!isMobile && (
          <div style={navLinksContainer}>
            <Link to="/" style={getLinkStyle('home', '/')} onMouseEnter={() => setHoveredLink('home')} onMouseLeave={() => setHoveredLink(null)}>Home</Link>
            <Link to="/why/" style={getLinkStyle('why', '/why')} onMouseEnter={() => setHoveredLink('why')} onMouseLeave={() => setHoveredLink(null)}>Why MDeRCM</Link>
            
            {/* SERVICES DROPDOWN */}
            <div className="services-dropdown-container" style={{ position: 'relative' }}>
              <span
                onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                onMouseEnter={() => setHoveredLink('services')}
                onMouseLeave={() => setHoveredLink(null)}
                style={{
                  ...getLinkStyle('services', '#'),
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Services ▾
              </span>

              {isServicesDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '12px 0',
                    minWidth: '300px',
                    zIndex: 1000,
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Link
                    to="/medical-billing-outsourcing/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    💼 Medical Billing Outsourcing
                  </Link>
                  
                  <Link
                    to="/hospital-revenue-cycle-management/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    🏥 Hospital RCM
                  </Link>
                  
                  <Link
                    to="/revenue-cycle-consulting/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    📊 RCM Consulting
                  </Link>

                  <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }}></div>
                  
                  <Link
                    to="/medical-billing-outsourcing-small-practices/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    👥 Small Practice Billing
                  </Link>
                  
                  <Link
                    to="/revenue-cycle-management-independent-physicians/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    👨‍⚕️ Independent Physician RCM
                  </Link>
                  
                  <Link
                    to="/revenue-cycle-management-multispecialty-practices/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    🏢 Multispecialty Practice RCM
                  </Link>

                  <Link
                    to="/denial-management-services-medical-clinics/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    🛡️ Denial Management
                  </Link>
                  
                  <Link
                    to="/reduce-claim-denials-healthcare-billing/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    📉 Reduce Claim Denials
                  </Link>
                  
                  <Link
                    to="/benefits-medical-billing-outsourcing-clinics/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#F26522';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    ⭐ Outsourcing Benefits
                  </Link>

                  <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }}></div>

                  <Link
                    to="/pricing/"
                    onClick={() => setIsServicesDropdownOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: '#F26522',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#FFF5F0';
                      e.target.style.paddingLeft = '25px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.paddingLeft = '20px';
                    }}
                  >
                    💰 View Pricing →
                  </Link>
                </div>
              )}
            </div>

            {/* AI FOR HEALTHCARE DROPDOWN */}
            <div className="ai-dropdown-container" style={{ position: 'relative' }}>
              <span
                onClick={() => setIsAIDropdownOpen(!isAIDropdownOpen)}
                style={{
                  ...getLinkStyle('ai', '/healthcare/ai'),
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                AI For Healthcare ▾
              </span>

              {isAIDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                    padding: '8px 0',
                    zIndex: 1001,
                    borderRadius: '8px',
                    width: '320px',
                  }}
                >
                  {[
                    'Insurance Verification AI Agent',
                    'Prior Authorization AI Agent',
                    'Insurance Contract & Repricing',
                    'Patient Intake AI Agent',
                    'Denials Management AI Agent',
                    'Payment Posting AI Agent',
                    'Accounts Receivable AI Agent',
                    'Compliance & Credentialling AI Agents',
                  ].map((label, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <Link
                        to={
                          label === 'Insurance Verification AI Agent'
                            ? '#'
                            : label === 'Prior Authorization AI Agent'
                            ? '/healthcare/ai/prior-authorization'
                            : label === 'Insurance Contract & Repricing'
                            ? '/healthcare/ai/InsuranceContract-Repricing'
                            : label === 'Patient Intake AI Agent'
                            ? '/healthcare/ai/patient-intake'
                            : label === 'Denials Management AI Agent'
                            ? '/healthcare/ai/denial-management'
                            : label === 'Payment Posting AI Agent'
                            ? '/healthcare/ai/payment-posting'
                            : label === 'Accounts Receivable AI Agent'
                            ? '/healthcare/ai/accounts-receivable'
                            : label === 'Compliance & Credentialling AI Agents'
                            ? '/healthcare/ai/compliance-agent'
                            : `/healthcare/ai#${label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '').replace(/[^\w-]/g, '')}`
                        }
                        style={{
                          display: 'block',
                          padding: '10px 20px',
                          fontSize: '13.5px',
                          color: '#000',
                          textDecoration: 'none',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          transition: 'color 0.2s ease',
                        }}
                        onClick={(e) => {
                          if (label === 'Insurance Verification AI Agent') {
                            e.preventDefault();
                            setOpenSubmenu(openSubmenu === label ? null : label);
                          } else {
                            setIsAIDropdownOpen(false);
                          }
                        }}
                        onMouseEnter={(e) => (e.target.style.color = '#F26522')}
                        onMouseLeave={(e) => (e.target.style.color = '#000')}
                      >
                        {label}
                        {label === 'Insurance Verification AI Agent' && ' ▸'}
                      </Link>

                      {openSubmenu === label && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '100%',
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                            minWidth: '240px',
                            padding: '8px 0',
                          }}
                        >
                          {['Eligibility Check', 'Policy Status Verification'].map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem === 'Eligibility Check'
                                ? '/healthcare/ai/eligibility-check'
                                : '/healthcare/ai/policy-status-verification'}
                              style={{
                                display: 'block',
                                padding: '10px 20px',
                                fontSize: '13.5px',
                                color: '#000',
                                textDecoration: 'none',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                transition: 'color 0.2s ease',
                              }}
                              onClick={() => setIsAIDropdownOpen(false)}
                              onMouseEnter={(e) => (e.target.style.color = '#F26522')}
                              onMouseLeave={(e) => (e.target.style.color = '#000')}
                            >
                              {subItem}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PREMIUM SOLUTIONS DROPDOWN */}
            <div className="premium-solutions-dropdown-container" style={{ position: 'relative' }}>
              <span
                onClick={() => setIsPremiumDropdownOpen(!isPremiumDropdownOpen)}
                style={{
                  ...getLinkStyle('premium', '/premium-solutions'),
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Premium Solutions ▾
              </span>

              {isPremiumDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                    padding: '8px 0',
                    zIndex: 1001,
                    borderRadius: '8px',
                    width: '320px',
                  }}
                >
                  {['Dental Health', 'Mental Health'].map((label, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <Link
                        to={label === 'Dental Health' ? '/Dental' : label === 'Mental Health' ? '/MentalHealth' : `/premium-solutions/${label.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{
                          display: 'block',
                          padding: '10px 20px',
                          fontSize: '13.5px',
                          color: '#000',
                          textDecoration: 'none',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          transition: 'color 0.2s ease',
                        }}
                        onMouseEnter={(e) => (e.target.style.color = '#F26522')}
                        onMouseLeave={(e) => (e.target.style.color = '#000')}
                      >
                        {label}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blog/" style={getLinkStyle('blog', '/blog')} onMouseEnter={() => setHoveredLink('blog')} onMouseLeave={() => setHoveredLink(null)}>News & Blog</Link>
            <Link to="/events/" style={getLinkStyle("events","/events")} onMouseEnter={() => setHoveredLink("events")} onMouseLeave={() => setHoveredLink(null)}>Events</Link>
            <Link to="/resources/" style={getLinkStyle('resources','/resources')} onMouseEnter={() => setHoveredLink('resources')} onMouseLeave={() => setHoveredLink(null)}>Resources</Link>
            <Link to="/company/" style={getLinkStyle('company','/company')} onMouseEnter={() => setHoveredLink('company')} onMouseLeave={() => setHoveredLink(null)}>Company</Link>
            <Link to="/compliance/" style={getLinkStyle('compliance','/compliance')} onMouseEnter={() => setHoveredLink('compliance')} onMouseLeave={() => setHoveredLink(null)}>Compliance</Link>
            <Link to="/careers/" style={getLinkStyle('careers','/careers')} onMouseEnter={() => setHoveredLink('careers')} onMouseLeave={() => setHoveredLink(null)}>Careers</Link>
            <Link to="/testimonials/" style={getLinkStyle('testimonials','/testimonials')} onMouseEnter={() => setHoveredLink('testimonials')} onMouseLeave={() => setHoveredLink(null)}>Testimonials</Link>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!isMobile && (
            <Link to="/free-trial/" style={{ textDecoration: 'none' }}>
              <button
                style={buttonStyle}
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
              >
                FREE TRIAL
              </button>
            </Link>
          )}
          {isMobile && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              onMouseEnter={() => setIsHamburgerHover(true)}
              onMouseLeave={() => setIsHamburgerHover(false)}
              style={hamburgerStyle}
            >
              ☰
            </button>
          )}
        </div>
      </nav>

      {/* MOBILE MENU */}
      {isMobile && menuOpen && (
        <div style={mobileMenuStyle}>
          <Link to="/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>🏠 Home</Link>
          <Link to="/why/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/why' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>⭐ Why MDeRCM</Link>

          {/* MOBILE SERVICES DROPDOWN */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                ...mobileMenuItemStyle,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 600,
                color: isMobileServicesDropdownOpen ? '#F26522' : '#000',
                backgroundColor: isMobileServicesDropdownOpen ? '#f8f9fa' : '#fff',
                ...(isMobileServicesDropdownOpen ? { borderLeft: '4px solid #F26522', paddingLeft: '16px' } : {})
              }}
              onClick={() => setIsMobileServicesDropdownOpen(!isMobileServicesDropdownOpen)}
            >
              <span>💼 Services</span>
              <span style={{ transform: isMobileServicesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▼</span>
            </span>

            {isMobileServicesDropdownOpen && (
              <div style={mobileSubMenuStyle}>
                <Link to="/medical-billing-outsourcing/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>💼 Medical Billing</Link>
                <Link to="/hospital-revenue-cycle-management/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>🏥 Hospital RCM</Link>
                <Link to="/revenue-cycle-consulting/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>📊 RCM Consulting</Link>
                <div style={{ borderTop: '1px solid #ddd', margin: '8px 0' }}></div>
                <Link to="/medical-billing-outsourcing-small-practices/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>👥 Small Practice</Link>
                <Link to="/revenue-cycle-management-independent-physicians/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>👨‍⚕️ Independent Physicians</Link>
                <Link to="/revenue-cycle-management-multispecialty-practices/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>🏢 Multispecialty</Link>
                <Link to="/denial-management-services-medical-clinics/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>🛡️ Denial Management</Link>
                <Link to="/reduce-claim-denials-healthcare-billing/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>📉 Reduce Denials</Link>
                <Link to="/benefits-medical-billing-outsourcing-clinics/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>⭐ Benefits</Link>
                <div style={{ borderTop: '1px solid #ddd', margin: '8px 0' }}></div>
                <Link to="/pricing/" onClick={() => setMenuOpen(false)} style={{ ...mobileSubMenuItemStyle, color: '#F26522', fontWeight: '600' }}>💰 Pricing</Link>
              </div>
            )}
          </div>

          {/* MOBILE AI FOR HEALTHCARE */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                ...mobileMenuItemStyle,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 600,
                color: isMobileAIDropdownOpen ? '#F26522' : '#000',
                backgroundColor: isMobileAIDropdownOpen ? '#f8f9fa' : '#fff',
                ...(isMobileAIDropdownOpen ? { borderLeft: '4px solid #F26522', paddingLeft: '16px' } : {})
              }}
              onClick={() => setIsMobileAIDropdownOpen(!isMobileAIDropdownOpen)}
            >
              <span>🤖 AI For Healthcare</span>
              <span style={{ transform: isMobileAIDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▼</span>
            </span>

            {isMobileAIDropdownOpen && (
              <div style={mobileSubMenuStyle}>
                {[
                  { label: 'Insurance Verification', submenu: ['Eligibility Check', 'Policy Status Verification'] },
                  { label: 'Prior Authorization', link: '/healthcare/ai/prior-authorization' },
                  { label: 'Insurance Contract & Repricing', link: '/healthcare/ai/InsuranceContract-Repricing' },
                  { label: 'Patient Intake', link: '/healthcare/ai/patient-intake' },
                  { label: 'Denials Management', link: '/healthcare/ai/denial-management' },
                  { label: 'Payment Posting', link: '/healthcare/ai/payment-posting' },
                  { label: 'Accounts Receivable', link: '/healthcare/ai/accounts-receivable' },
                  { label: 'Compliance & Credentialling', link: '/healthcare/ai/compliance-agent' }
                ].map((item, index) => (
                  item.submenu ? (
                    <div key={index}>
                      <span
                        onClick={() => setOpenMobileSubmenu(openMobileSubmenu === item.label ? null : item.label)}
                        style={{
                          ...mobileSubMenuItemStyle,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <span>{item.label}</span>
                        <span style={{ transform: openMobileSubmenu === item.label ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▼</span>
                      </span>
                      {openMobileSubmenu === item.label && (
                        <div style={mobileNestedSubMenuStyle}>
                          {item.submenu.map((sub, subIdx) => (
                            <Link
                              key={subIdx}
                              to={sub === 'Eligibility Check' ? '/healthcare/ai/eligibility-check' : '/healthcare/ai/policy-status-verification'}
                              onClick={() => setMenuOpen(false)}
                              style={mobileNestedSubMenuItemStyle}
                            >
                              {sub}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={index}
                      to={item.link}
                      onClick={() => setMenuOpen(false)}
                      style={mobileSubMenuItemStyle}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>

          {/* MOBILE PREMIUM SOLUTIONS */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                ...mobileMenuItemStyle,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 600,
                color: isMobilePremiumDropdownOpen ? '#F26522' : '#000',
                backgroundColor: isMobilePremiumDropdownOpen ? '#f8f9fa' : '#fff',
                ...(isMobilePremiumDropdownOpen ? { borderLeft: '4px solid #F26522', paddingLeft: '16px' } : {})
              }}
              onClick={() => setIsMobilePremiumDropdownOpen(!isMobilePremiumDropdownOpen)}
            >
              <span>💎 Premium Solutions</span>
              <span style={{ transform: isMobilePremiumDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▼</span>
            </span>

            {isMobilePremiumDropdownOpen && (
              <div style={mobileSubMenuStyle}>
                <Link to="/Dental/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>🦷 Dental Health</Link>
                <Link to="/MentalHealth/" onClick={() => setMenuOpen(false)} style={mobileSubMenuItemStyle}>🧠 Mental Health</Link>
              </div>
            )}
          </div>

          <Link to="/blog/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/blog' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>📰 News & Blog</Link>
          <Link to="/events/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/events' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>🎉 Events</Link>
          <Link to="/resources/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/resources' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>📚 Resources</Link>
          <Link to="/company/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/company' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>🏢 Company</Link>
          <Link to="/compliance/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/compliance' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>🏆 Compliance</Link>
          <Link to="/careers/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/careers' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>💼 Careers</Link>
          <Link to="/testimonials/" style={{ ...mobileMenuItemStyle, ...(location.pathname === '/testimonials' ? mobileMenuItemHoverStyle : {}) }} onClick={() => setMenuOpen(false)}>⭐ Testimonials</Link>
          
          <Link to="/free-trial/" style={{ ...mobileMenuItemStyle, backgroundColor: '#F26522', color: '#fff', fontWeight: 600, textAlign: 'center', borderRadius: '8px', margin: '10px 20px', borderBottom: 'none' }} onClick={() => setMenuOpen(false)}>🚀 FREE TRIAL</Link>
        </div>
      )}

      {/* Responsive CSS for Header Adjustments */}
      <style>
        {`
          @media (max-width: 1400px) {
            nav > div:first-of-type a,
            nav > div:first-of-type > div > span {
              font-size: 12.5px !important;
              padding: 0 !important;
            }
            nav > div:first-of-type {
              gap: 16px !important;
            }
          }
          
          @media (max-width: 1200px) {
            nav > div:first-of-type a,
            nav > div:first-of-type > div > span {
              font-size: 12px !important;
            }
            nav > div:first-of-type {
              gap: 14px !important;
            }
          }
          
          @media (max-width: 1100px) {
            nav > div:first-of-type a,
            nav > div:first-of-type > div > span {
              font-size: 11.5px !important;
            }
            nav > div:first-of-type {
              gap: 12px !important;
            }
          }
          
          @media (max-width: 1024px) {
            nav > div:first-of-type a,
            nav > div:first-of-type > div > span {
              font-size: 11px !important;
            }
            nav > div:first-of-type {
              gap: 10px !important;
            }
          }
          
          @media (max-width: 900px) {
            nav > div:first-of-type a,
            nav > div:first-of-type > div > span {
              font-size: 10.5px !important;
            }
            nav > div:first-of-type {
              gap: 8px !important;
            }
          }
        `}
      </style>
    </header>
  );
};

export default Header;